import { SortingRule } from 'react-table';
import {
  createLinkDataMap,
  fetchParentSchemas,
  fetchChildLinkedRecords,
  fetchParentLinkedRecords,
} from '../../lib/linked-record-helper';
import sqlBuilder from '../../tasks/sql-builder';
import { loadColumns } from '../../hooks/use-form-app';
import homeFilters from '../../home-page/home-filters';
import { formsApiClient } from '../fetch/forms-api-client';
import { QueryColumnResponse, RecordFilters, RestApiForm } from '../../tasks/types';

export interface Records {
  restForm?: RestApiForm;
  queryColumns?: QueryColumnResponse;
  recordFilters?: RecordFilters;
  sortingInstructions?: SortingRule<object>[];
  pageIndex: number;
  app: any;
  pageSize?: number;
  isAppLink?: boolean;
}

const getRecords = async ({
  restForm, queryColumns, recordFilters, sortingInstructions,
  pageIndex, app, pageSize = 100, isAppLink,
}: Records) => {
  const apiClient = formsApiClient(app?.api?.token);

  if (!restForm || !queryColumns) {
    return { data: [] };
  }

  const loadData = async () => {
    let flatLinkedRecords = [];

    if (restForm.form_links?.to) {
      const linkedFormsToIds = restForm.form_links.to;

      // Fetch forms if the form is linked, otherwise we fetch the system apps
      const formsPromises = linkedFormsToIds.map(async (linkedForm: { form_id: string; }) => ({
        form: isAppLink
          ? await apiClient.fetchForm(linkedForm.form_id)
          : await apiClient.fetchSystemApp(linkedForm.form_id),
        columns: await loadColumns(app.dataSource, linkedForm.form_id),
      }));

      const linkedForms = await Promise.all([...formsPromises]);

      const linkedRecordsSqls = linkedForms.map((linkedForm) => app.api.query.queryRecords({
        q: sqlBuilder({
          restForm: linkedForm.form.form,
          queryColumns: linkedForm.columns,
          recordFilters: { filters: [], globalFilter: '' },
          additionalFilters: homeFilters.completedTasks,
          // @ts-ignore
        }).toSQL({}),
      }));

      const linkedRecords = await Promise.all(linkedRecordsSqls);
      // @ts-ignore
      flatLinkedRecords = linkedRecords.flat().map((linkedRecord) => linkedRecord.id);
    }

    const query = sqlBuilder({
      restForm,
      linkedRecords: flatLinkedRecords,
      queryColumns,
      recordFilters,
      sortingInstructions,
    });

    const countSql = query.toCountSQL();
    // @ts-ignore
    const sql = query.toSQL({ applySort: true, pageSize, pageIndex });

    const [count, recordArray] = await Promise.all([
      app.api.query.queryRecords({ q: countSql }),
      app.api.query.queryRecords({ q: sql }),
    ]);

    if (restForm.form_links) {
      const formLinks = restForm.form_links;
      const linkDataMap = createLinkDataMap(recordArray, formLinks);
      const parentSchemas = await fetchParentSchemas(formLinks, app);

      const [to, from] = await Promise.all([
        fetchChildLinkedRecords(linkDataMap, app),
        fetchParentLinkedRecords(linkDataMap, parentSchemas, formLinks, app),
      ]);

      recordArray.forEach((rec: any) => {
        if (to[rec.id]) {
          // eslint-disable-next-line no-param-reassign
          rec.to = to[rec.id];
        }
        if (from[rec.id]) {
          // eslint-disable-next-line no-param-reassign
          rec.from = from[rec.id];
        }
      });
    }

    const fetchedRecords = { ...count[0], records: recordArray };
    return {
      data: fetchedRecords,
    };
  };

  return loadData();
};

export default getRecords;
